import { 
  db, 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  writeBatch,
  Timestamp
} from '../firebase/config';
import { generateAssignmentId } from '../utils/idGenerator';
import { 
  ProjectAssignment, 
  ProjectAssignmentRecord, 
  ProjectAssignmentFormData,
  ProjectAssignmentSubTaskSummary,
  ProjectAssignmentInfo,
  AssignmentStatusValue,
  SubTask,
  AssignmentStudentStatus
} from '../types';
import { optimizedAssignmentServiceV3 } from './optimizedAssignmentServiceV3';
import { ProjectAssignmentConsolidatedService } from './projectAssignmentConsolidatedService';
import { normalizeMemberGroup } from '../utils/memberGroupPaths';
import { assignmentMemoService } from './assignmentMemoService';

class ProjectAssignmentService {
  
  // ===== 프로젝트 과제 CRUD =====

  /**
   * 프로젝트 과제 생성
   */
  async createProjectAssignment(
    userId: string,
    classId: string,
    formData: ProjectAssignmentFormData,
    memberGroup?: string,
    assignmentId?: string
  ): Promise<string> {
    try {
      const g = normalizeMemberGroup(memberGroup);
      const projectId = assignmentId || generateAssignmentId('project');
      
      const projectAssignment: ProjectAssignment = {
        id: projectId,
        title: formData.title,
        description: formData.description,
        dueDate: formData.dueDate,
        assignedStudentIds: formData.assignedStudentIds,
        subTasks: formData.subTasks,
        color: formData.color,
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true,
        classId
      };

      // 새로운 프로젝트 보상 필드
      if (formData.rewardEnabled !== undefined) {
        projectAssignment.rewardEnabled = formData.rewardEnabled;
      }
      if (formData.rewardMessage && formData.rewardMessage.trim()) {
        projectAssignment.rewardMessage = formData.rewardMessage;
      }
      if (formData.rewardCoins !== undefined && formData.rewardCoins > 0) {
        projectAssignment.rewardCoins = formData.rewardCoins;
      }
      if (formData.rewardItemCardIds && formData.rewardItemCardIds.length > 0) {
        projectAssignment.rewardItemCardIds = formData.rewardItemCardIds;
      }

      // 통합 서비스 사용
      const consolidatedService = new ProjectAssignmentConsolidatedService(userId, classId, g);
      const docId = await consolidatedService.createProjectAssignment(projectAssignment, formData.subTasks);

      console.log('✅ 통합 서비스: 프로젝트 과제 생성 완료:', {
        projectId,
        docId,
        subTaskCount: formData.subTasks.length
      });

      return projectId;
    } catch (error) {
      console.error('❌ 통합 서비스: 프로젝트 과제 생성 실패:', error);
      throw error;
    }
  }

  /**
   * 프로젝트 과제 수정
   */
  async updateProjectAssignment(
    userId: string,
    classId: string,
    projectAssignmentId: string,
    formData: ProjectAssignmentFormData,
    memberGroup?: string
  ): Promise<void> {
    try {
      // 새로운 통합 구조 사용
      const consolidatedService = new ProjectAssignmentConsolidatedService(
        userId,
        classId,
        normalizeMemberGroup(memberGroup)
      );
      
      await consolidatedService.updateProjectAssignment(
        projectAssignmentId,
        formData.title,
        formData.description || '',
        formData.dueDate,
        formData.assignedStudentIds,
        formData.subTasks || [],
        formData.color,
        true, // 기본값으로 isActive = true 사용
        formData.rewardEnabled,
        formData.rewardMessage,
        formData.rewardCoins,
        formData.rewardItemCardIds
      );

      console.log('✅ 프로젝트 과제 수정 완료 (통합 구조):', {
        projectAssignmentId,
        title: formData.title,
        subTaskCount: formData.subTasks?.length || 0,
        studentCount: formData.assignedStudentIds.length
      });
    } catch (error) {
      console.error('❌ 프로젝트 과제 수정 실패 (통합 구조):', error);
      throw error;
    }
  }

  /**
   * 프로젝트 과제 삭제
   */
  async deleteProjectAssignment(
    userId: string,
    classId: string,
    projectAssignmentId: string,
    memberGroup?: string
  ): Promise<void> {
    try {
      // 통합 서비스 사용
      const consolidatedService = new ProjectAssignmentConsolidatedService(
        userId,
        classId,
        normalizeMemberGroup(memberGroup)
      );
      await consolidatedService.deleteProjectAssignment(projectAssignmentId);

      console.log('✅ 통합 서비스: 프로젝트 과제 삭제 완료:', {
        projectAssignmentId
      });
    } catch (error) {
      console.error('❌ 통합 서비스: 프로젝트 과제 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 클래스의 모든 프로젝트 과제 조회
   */
  async getProjectAssignments(
    userId: string,
    classId: string,
    memberGroup?: string
  ): Promise<ProjectAssignment[]> {
    try {
      // 통합 서비스 사용
      const consolidatedService = new ProjectAssignmentConsolidatedService(
        userId,
        classId,
        normalizeMemberGroup(memberGroup)
      );
      const projectAssignmentInfos = await consolidatedService.loadAllProjectAssignments();
      
      // ProjectAssignmentInfo를 ProjectAssignment로 변환 (submittedCount 포함)
      const projectAssignments: ProjectAssignment[] = projectAssignmentInfos.map(info => {
        // 모든 서브태스크의 제출 확인 멤버 수 합계 계산
        const submittedCount = info.subTasks.reduce((total, subTask) => {
          const subTaskSubmittedCount = subTask.studentStatuses.filter(s => s.status === 'submitted').length;
          return total + subTaskSubmittedCount;
        }, 0);
        
        return {
          id: info.assignmentId,
          title: info.title,
          description: info.description,
          dueDate: new Date(info.dueDate),
          assignedStudentIds: info.assignedStudentIds,
          subTasks: info.subTasks.map(subTask => ({
            id: subTask.subTaskId,
            order: subTask.subTaskOrder,
            title: subTask.subTaskTitle,
            points: subTask.subTaskBasePoints, // SubTask에서는 points 필드 사용
            studentStatuses: subTask.studentStatuses.map(status => ({
              id: status.studentId,
              studentId: status.studentId,
              assignmentId: info.assignmentId,
              status: status.status,
              earnedPoints: status.earnedPoints,
              memo: status.memo,
              updatedAt: status.lastUpdated
            }))
          })),
          color: info.color,
          createdAt: info.createdAt,
          updatedAt: info.updatedAt,
          isActive: info.isActive,
          classId: info.classId,
          submittedCount, // 전체 서브태스크의 제출 확인 합계
          // 프로젝트 보상 시스템
          rewardEnabled: info.rewardEnabled,
          rewardMessage: info.rewardMessage,
          rewardCoins: info.rewardCoins,
          rewardItemCardIds: info.rewardItemCardIds,
          rewardGrantedStudentIds: info.rewardGrantedStudentIds
        };
      });

      console.log('✅ 통합 서비스: 프로젝트 과제 목록 조회 완료:', {
        count: projectAssignments.length
      });

      return projectAssignments;
    } catch (error) {
      console.error('❌ 통합 서비스: 프로젝트 과제 목록 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 프로젝트 과제 조회
   */
  async getProjectAssignment(
    userId: string,
    classId: string,
    projectAssignmentId: string,
    memberGroup?: string
  ): Promise<ProjectAssignment | null> {
    try {
      // 통합 서비스 사용
      const consolidatedService = new ProjectAssignmentConsolidatedService(
        userId,
        classId,
        normalizeMemberGroup(memberGroup)
      );
      const projectAssignmentInfo = await consolidatedService.loadProjectAssignment(projectAssignmentId);
      
      if (!projectAssignmentInfo) {
        return null;
      }

      // ProjectAssignmentInfo를 ProjectAssignment로 변환
      const projectAssignment: ProjectAssignment = {
        id: projectAssignmentInfo.assignmentId,
        title: projectAssignmentInfo.title,
        description: projectAssignmentInfo.description,
        dueDate: new Date(projectAssignmentInfo.dueDate),
        assignedStudentIds: projectAssignmentInfo.assignedStudentIds,
        subTasks: projectAssignmentInfo.subTasks.map(subTask => ({
          id: subTask.subTaskId,
          order: subTask.subTaskOrder,
          title: subTask.subTaskTitle,
          points: subTask.subTaskBasePoints, // SubTask에서는 points 필드 사용
          studentStatuses: subTask.studentStatuses.map(status => ({
            id: status.studentId,
            studentId: status.studentId,
            assignmentId: projectAssignmentInfo.assignmentId,
            status: status.status,
            earnedPoints: status.earnedPoints,
            memo: status.memo,
            updatedAt: status.lastUpdated
          }))
        })),
        color: projectAssignmentInfo.color,
        createdAt: projectAssignmentInfo.createdAt,
        updatedAt: projectAssignmentInfo.updatedAt,
        isActive: projectAssignmentInfo.isActive,
        classId: projectAssignmentInfo.classId,
        // 프로젝트 보상 시스템
        rewardEnabled: projectAssignmentInfo.rewardEnabled,
        rewardMessage: projectAssignmentInfo.rewardMessage,
        rewardCoins: projectAssignmentInfo.rewardCoins,
        rewardItemCardIds: projectAssignmentInfo.rewardItemCardIds,
        rewardGrantedStudentIds: projectAssignmentInfo.rewardGrantedStudentIds
      };

      console.log('✅ 통합 서비스: 프로젝트 과제 조회 완료:', {
        projectAssignmentId,
        title: projectAssignment.title
      });

      return projectAssignment;
    } catch (error) {
      console.error('❌ 통합 서비스: 프로젝트 과제 조회 실패:', error);
      throw error;
    }
  }

  // ===== 프로젝트 과제 상태 관리 =====

  /**
   * 프로젝트 과제의 하위 과제별 상태 초기화
   */
  private async initializeProjectAssignmentStatuses(
    userId: string,
    classId: string,
    projectAssignment: ProjectAssignment
  ): Promise<void> {
    try {
      const batch = writeBatch(db);
      const statusRef = doc(db, 'users', userId, 'classes', classId, 'projectAssignmentStatuses', projectAssignment.id);
      
      // 프로젝트 과제 상태 문서 생성
      batch.set(statusRef, {
        projectAssignmentId: projectAssignment.id,
        createdAt: Timestamp.fromDate(new Date()),
        updatedAt: Timestamp.fromDate(new Date())
      });

      // 각 하위 과제에 대해 멤버 상태 배열로 생성
      for (const subTask of projectAssignment.subTasks) {
        const subTaskRef = doc(collection(statusRef, 'subTasks'), subTask.id);
        
        // 멤버 상태 배열 생성 (과제에 배정된 멤버들만) - memo는 assignmentMemo 컬렉션에서 관리
        const studentStatuses = projectAssignment.assignedStudentIds.map(studentId => ({
          studentId,
          status: 'pending' as AssignmentStatusValue,
          earnedPoints: 0,
          lastUpdated: new Date()
        }));
        
        batch.set(subTaskRef, {
          subTaskId: subTask.id,
          subTaskTitle: subTask.title,
          basePoints: subTask.points, // subTaskPoints → basePoints로 통일
          subTaskOrder: subTask.order - 1, // 클라이언트는 1부터 시작, 서버는 0부터 시작
          studentStatuses, // 배열로 저장
          createdAt: Timestamp.fromDate(new Date()),
          // 테이블 로드를 위한 메타데이터 추가
          projectColor: projectAssignment.color,
          projectDueDate: projectAssignment.dueDate,
          projectTitle: projectAssignment.title,
          assignedStudentIds: projectAssignment.assignedStudentIds
        });
      }

      await batch.commit();
    } catch (error) {
      console.error('프로젝트 과제 상태 초기화 실패:', error);
      throw error;
    }
  }

  /**
   * 프로젝트 과제 상태 업데이트 (하위 과제 변경 시)
   */
  private async updateProjectAssignmentStatuses(
    userId: string,
    classId: string,
    projectAssignmentId: string,
    formData: ProjectAssignmentFormData
  ): Promise<void> {
    try {
      const batch = writeBatch(db);
      const statusRef = doc(db, 'users', userId, 'classes', classId, 'projectAssignmentStatuses', projectAssignmentId);
      
      // 기존 하위 과제들 조회
      const existingSubTasksRef = collection(statusRef, 'subTasks');
      const existingSubTasksSnap = await getDocs(existingSubTasksRef);
      
      // 기존 하위 과제 ID와 새 하위 과제 ID 매핑
      const existingSubTaskIds = new Set(existingSubTasksSnap.docs.map(doc => doc.id));
      const newSubTaskIds = new Set(formData.subTasks.map(st => st.id));
      
      // 삭제할 하위 과제들 (기존에 있지만 새 목록에 없는 것들)
      const subTasksToDelete = Array.from(existingSubTaskIds).filter(id => !newSubTaskIds.has(id));
      
      // 삭제할 하위 과제들 삭제 (studentStatuses 배열이므로 별도 멤버 데이터 삭제 불필요)
      for (const subTaskId of subTasksToDelete) {
        const subTaskRef = doc(existingSubTasksRef, subTaskId);
        batch.delete(subTaskRef);
      }

      // 기존 하위 과제들 업데이트 및 새 하위 과제들 생성
      for (const subTask of formData.subTasks) {
        const subTaskRef = doc(collection(statusRef, 'subTasks'), subTask.id);
        
        // 멤버 상태 배열 생성 (과제에 배정된 멤버들만) - memo는 assignmentMemo 컬렉션에서 관리
        const studentStatuses = formData.assignedStudentIds.map(studentId => ({
          studentId,
          status: 'pending' as AssignmentStatusValue,
          earnedPoints: 0,
          lastUpdated: new Date()
        }));

        if (existingSubTaskIds.has(subTask.id)) {
          // 기존 하위 과제: 제목, 포인트, 멤버 상태 배열 업데이트 (메타데이터 포함)
          batch.update(subTaskRef, {
            subTaskTitle: subTask.title,
            basePoints: subTask.points, // subTaskPoints → basePoints로 통일
            subTaskOrder: subTask.order - 1, // 클라이언트는 1부터 시작, 서버는 0부터 시작
            studentStatuses, // 배열로 업데이트
            updatedAt: Timestamp.fromDate(new Date()),
            // 테이블 로드를 위한 메타데이터 추가
            projectColor: formData.color,
            projectDueDate: formData.dueDate,
            projectTitle: formData.title,
            assignedStudentIds: formData.assignedStudentIds
          });
        } else {
          // 새 하위 과제: 완전히 새로 생성 (메타데이터 포함)
          batch.set(subTaskRef, {
            subTaskId: subTask.id,
            subTaskTitle: subTask.title,
            basePoints: subTask.points, // subTaskPoints → basePoints로 통일
            subTaskOrder: subTask.order - 1, // 클라이언트는 1부터 시작, 서버는 0부터 시작
            studentStatuses, // 배열로 저장
            createdAt: Timestamp.fromDate(new Date()),
            // 테이블 로드를 위한 메타데이터 추가
            projectColor: formData.color,
            projectDueDate: formData.dueDate,
            projectTitle: formData.title,
            assignedStudentIds: formData.assignedStudentIds
          });
        }
      }

      await batch.commit();
    } catch (error) {
      console.error('프로젝트 과제 상태 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 프로젝트 과제의 하위 과제별 요약 정보 조회
   */
  async loadProjectAssignmentWithStatuses(
    userId: string,
    classId: string,
    projectAssignmentId: string,
    memberGroup?: string
  ): Promise<{
    projectAssignment: ProjectAssignment;
    subTaskSummaries: ProjectAssignmentSubTaskSummary[];
    /** 보상 지급 등에서 재사용 가능한 원본 정보 (캐시용) */
    projectAssignmentInfo: ProjectAssignmentInfo;
  }> {
    try {
      // 통합 서비스 사용
      const consolidatedService = new ProjectAssignmentConsolidatedService(userId, classId, memberGroup);
      const projectAssignmentInfo = await consolidatedService.loadProjectAssignment(projectAssignmentId);
      
      if (!projectAssignmentInfo) {
        throw new Error('프로젝트 과제를 찾을 수 없습니다.');
      }

      // ProjectAssignmentInfo를 ProjectAssignment로 변환
      const projectAssignment: ProjectAssignment = {
        id: projectAssignmentInfo.assignmentId,
        title: projectAssignmentInfo.title,
        description: projectAssignmentInfo.description,
        dueDate: new Date(projectAssignmentInfo.dueDate),
        assignedStudentIds: projectAssignmentInfo.assignedStudentIds,
        subTasks: projectAssignmentInfo.subTasks.map(subTask => ({
          id: subTask.subTaskId,
          order: subTask.subTaskOrder,
          title: subTask.subTaskTitle,
          points: subTask.subTaskBasePoints,
          studentStatuses: subTask.studentStatuses.map(status => ({
            id: status.studentId,
            studentId: status.studentId,
            assignmentId: projectAssignmentInfo.assignmentId,
            status: status.status,
            earnedPoints: status.earnedPoints,
            memo: status.memo,
            updatedAt: status.lastUpdated
          }))
        })),
        color: projectAssignmentInfo.color,
        createdAt: projectAssignmentInfo.createdAt,
        updatedAt: projectAssignmentInfo.updatedAt,
        isActive: projectAssignmentInfo.isActive,
        classId: projectAssignmentInfo.classId,
        rewardEnabled: projectAssignmentInfo.rewardEnabled,
        rewardMessage: projectAssignmentInfo.rewardMessage,
        rewardCoins: projectAssignmentInfo.rewardCoins,
        rewardItemCardIds: projectAssignmentInfo.rewardItemCardIds,
        rewardGrantedStudentIds: projectAssignmentInfo.rewardGrantedStudentIds
      };

      // 하위 과제별 상태 요약 생성
      const subTaskSummaries: ProjectAssignmentSubTaskSummary[] = [];

      for (const subTask of projectAssignmentInfo.subTasks) {
        // studentStatuses 배열에서 멤버 상태 조회
        const studentStatuses = subTask.studentStatuses || [];

        let totalStudents = 0;
        let completedStudents = 0;
        let pendingStudents = 0;
        let delayedStudents = 0;
        let exemptedStudents = 0;
        let excludedStudents = 0;
        let submittedStudents = 0;

        studentStatuses.forEach((studentStatus: any) => {
          totalStudents++;

          switch (studentStatus.status) {
            case 'confirmed':
              completedStudents++;
              break;
            case 'pending':
              pendingStudents++;
              break;
            case 'delayed':
              delayedStudents++;
              break;
            case 'exempted':
              exemptedStudents++;
              break;
            case 'excluded':
              excludedStudents++;
              break;
            case 'submitted':
              submittedStudents++;
              pendingStudents++; // 제출 확인 상태도 미확인으로 취급
              break;
          }
        });

        // SubTask 객체 생성 (통합 문서 구조의 필드명 사용)
        const subTaskObj: SubTask = {
          id: subTask.subTaskId,
          order: subTask.subTaskOrder,
          title: subTask.subTaskTitle,
          points: subTask.subTaskBasePoints
        };

        subTaskSummaries.push({
          subTask: subTaskObj,
          totalStudents,
          completedStudents,
          pendingStudents,
          delayedStudents,
          exemptedStudents,
          excludedStudents,
          submittedStudents
        });
      }

      // 하위 과제 순서 정렬 (서버에서 저장된 order 정보 사용)
      subTaskSummaries.sort((a, b) => {
        return a.subTask.order - b.subTask.order;
      });

      return {
        projectAssignment,
        subTaskSummaries,
        projectAssignmentInfo
      };
    } catch (error) {
      console.error('프로젝트 과제 상태 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 프로젝트 과제의 특정 하위 과제 삭제
   */
  async deleteProjectAssignmentSubTask(
    userId: string,
    classId: string,
    projectAssignmentId: string,
    subTaskId: string
  ): Promise<void> {
    try {
      const batch = writeBatch(db);

      const statusRef = doc(db, 'users', userId, 'classes', classId, 'projectAssignmentStatuses', projectAssignmentId);
      const subTaskRef = doc(collection(statusRef, 'subTasks'), subTaskId);
      
      const subTaskDoc = await getDoc(subTaskRef);
      if (!subTaskDoc.exists()) {
        return;
      }

      // 하위 과제 문서 삭제 (studentStatuses 배열이므로 별도 멤버 데이터 삭제 불필요)
      batch.delete(subTaskRef);

      await batch.commit();
    } catch (error) {
      console.error('프로젝트 과제 하위 과제 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 프로젝트 과제의 멤버별 상태 조회 (특정 하위 과제)
   */
  async getProjectAssignmentStudentStatuses(
    userId: string,
    classId: string,
    projectAssignmentId: string,
    subTaskId: string
  ): Promise<ProjectAssignmentRecord[]> {
    try {
      const statusRef = doc(db, 'users', userId, 'classes', classId, 'projectAssignmentStatuses', projectAssignmentId);
      const subTaskRef = doc(collection(statusRef, 'subTasks'), subTaskId);
      
      const subTaskDoc = await getDoc(subTaskRef);
      if (!subTaskDoc.exists()) {
        return [];
      }
      
      const data = subTaskDoc.data();
      const studentStatuses = data.studentStatuses || [];
      const memoMap = await assignmentMemoService.getMemoMapForAssignment(
        userId,
        classId,
        `${projectAssignmentId}-${subTaskId}`
      );
      
      // studentStatuses 배열을 ProjectAssignmentRecord 형태로 변환
      return studentStatuses.map((status: any) => ({
        id: status.studentId,
        projectAssignmentId,
        subTaskId,
        studentId: status.studentId,
        status: status.status,
        earnedPoints: status.earnedPoints || 0,
        memo: memoMap?.[status.studentId]?.memo || status.memo || '',
        updatedAt: status.lastUpdated ? status.lastUpdated.toDate() : new Date(),
        confirmedAt: status.status === 'confirmed' ? status.lastUpdated?.toDate() : undefined
      } as ProjectAssignmentRecord));
    } catch (error) {
      console.error('프로젝트 과제 멤버 상태 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 프로젝트 과제의 멤버 상태 업데이트
   */
  async updateProjectAssignmentStudentStatus(
    userId: string,
    classId: string,
    projectAssignmentId: string,
    subTaskId: string,
    studentId: string,
    status: ProjectAssignmentRecord['status'],
    earnedPoints: number,
    memo?: string,
    memberGroup?: string
  ): Promise<void> {
    try {
      // 통합 서비스 사용
      const consolidatedService = new ProjectAssignmentConsolidatedService(userId, classId, memberGroup);
      await assignmentMemoService.saveMemo(
        userId,
        classId,
        `${projectAssignmentId}-${subTaskId}`,
        studentId,
        memo || '',
        undefined,
        undefined,
        memberGroup
      );
      await consolidatedService.updateSubTaskStudentStatus(
        projectAssignmentId,
        subTaskId,
        studentId,
        status as AssignmentStatusValue,
        earnedPoints,
        ''
      );

      console.log('✅ 통합 서비스: 프로젝트 과제 멤버 상태 업데이트 완료:', {
        projectAssignmentId,
        subTaskId,
        studentId,
        status,
        earnedPoints
      });
    } catch (error) {
      console.error('❌ 통합 서비스: 프로젝트 과제 멤버 상태 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 프로젝트 과제의 특정 하위 과제 멤버 상태들 로드
   */
  async loadProjectAssignmentStudentStatuses(
    userId: string,
    classId: string,
    projectAssignmentId: string,
    subTaskId: string,
    memberGroup?: string
  ): Promise<AssignmentStudentStatus[]> {
    try {
      console.log('🔍 프로젝트 과제 멤버 상태 로드 시작 (통합 문서 구조):', {
        userId,
        classId,
        projectAssignmentId,
        subTaskId
      });

      // 통합 서비스 사용
      const consolidatedService = new ProjectAssignmentConsolidatedService(userId, classId, memberGroup);
      const projectAssignmentInfo = await consolidatedService.loadProjectAssignment(projectAssignmentId);
      
      if (!projectAssignmentInfo) {
        console.log('프로젝트 과제를 찾을 수 없습니다.');
        return [];
      }

      // 해당 하위 과제 찾기
      const targetSubTask = projectAssignmentInfo.subTasks.find(subTask => subTask.subTaskId === subTaskId);
      
      if (!targetSubTask) {
        console.log('하위 과제를 찾을 수 없습니다.');
        return [];
      }

      const studentStatuses = targetSubTask.studentStatuses || [];
      const memoMap = await assignmentMemoService.getMemoMapForAssignment(
        userId,
        classId,
        `${projectAssignmentId}-${subTaskId}`,
        memberGroup
      );
      
      // studentStatuses 배열을 AssignmentStudentStatus 형태로 변환
      const result: AssignmentStudentStatus[] = studentStatuses.map((status: any) => {
        const updatedAt = status.lastUpdated?.toDate ? status.lastUpdated.toDate() : new Date();
        const submittedAt = status.submittedAt?.toDate ? status.submittedAt.toDate() : status.submittedAt;
        const memoEntry = memoMap?.[status.studentId];
        
        return {
          id: status.studentId,
          studentId: status.studentId,
          assignmentId: projectAssignmentId,
          status: status.status || 'pending',
          earnedPoints: status.points ?? status.earnedPoints ?? 0,
          memo: memoEntry?.memo || status.memo || '',
          notes: status.notes || '',
          submissionLink: status.submissionLink,
          submissionMessage: status.submissionMessage,
          submissionImagePath: status.submissionImagePath,
          submissionImageUpdatedAt: status.submissionImageUpdatedAt,
          submissionImageSizeBytes: status.submissionImageSizeBytes,
          submittedAt,
          updatedAt
        };
      });

      console.log('✅ 프로젝트 과제 멤버 상태 로드 완료:', result);
      return result;
    } catch (error) {
      console.error('프로젝트 과제 멤버 상태 로드 실패:', error);
      // 에러가 발생해도 빈 배열 반환 (기존 상태가 없는 경우)
      return [];
    }
  }

  /**
   * 멤버 삭제 시 프로젝트 과제 관련 데이터 정리
   */
  async cleanupStudentProjectAssignmentData(
    userId: string,
    classId: string,
    studentIds: string[]
  ): Promise<void> {
    try {
      const batch = writeBatch(db);

      // 모든 프로젝트 과제 조회
      const projectAssignmentsRef = collection(db, 'users', userId, 'classes', classId, 'projectAssignments');
      const projectAssignmentsSnapshot = await getDocs(projectAssignmentsRef);

      for (const projectAssignmentDoc of projectAssignmentsSnapshot.docs) {
        const projectAssignmentId = projectAssignmentDoc.id;
        const projectAssignmentData = projectAssignmentDoc.data();

        // 현재 프로젝트 과제의 assignedStudentIds 확인
        const currentAssignedIds = projectAssignmentData.assignedStudentIds || [];

        // 삭제할 멤버들을 제외한 새로운 assignedStudentIds 생성
        const updatedAssignedIds = currentAssignedIds.filter((id: string) => !studentIds.includes(id));

        if (updatedAssignedIds.length !== currentAssignedIds.length) {
          // assignedStudentIds가 변경된 경우에만 업데이트
          const projectAssignmentRef = doc(db, 'users', userId, 'classes', classId, 'projectAssignments', projectAssignmentId);
          batch.update(projectAssignmentRef, {
            assignedStudentIds: updatedAssignedIds,
            updatedAt: Timestamp.fromDate(new Date())
          });
        }

        // 프로젝트 과제 상태 참조
        const statusRef = doc(db, 'users', userId, 'classes', classId, 'projectAssignmentStatuses', projectAssignmentId);
        const subTasksRef = collection(statusRef, 'subTasks');
        const subTasksSnapshot = await getDocs(subTasksRef);

        // 각 하위 과제의 멤버 데이터 삭제
        for (const subTaskDoc of subTasksSnapshot.docs) {
          // 각 삭제할 멤버의 상태 데이터 삭제
          for (const studentId of studentIds) {
            const studentStatusRef = doc(collection(subTaskDoc.ref, 'students'), studentId);
            const studentStatusSnapshot = await getDoc(studentStatusRef);

            if (studentStatusSnapshot.exists()) {
              batch.delete(studentStatusRef);
            }
          }
        }
      }

      // 배치 커밋
      await batch.commit();
    } catch (error) {
      console.error('멤버 프로젝트 과제 데이터 정리 실패:', error);
      throw error;
    }
  }

  /**
   * 최적화된 프로젝트 하위 과제 멤버 상태 업데이트 (새로운 배열 기반 구조)
   */
  async updateProjectAssignmentStudentStatusOptimized(
    userId: string,
    classId: string,
    projectId: string,
    subTaskId: string,
    studentId: string,
    status: AssignmentStatusValue,
    earnedPoints: number = 0,
    memo: string = '',
    memberGroup?: string
  ): Promise<void> {
    try {
      await optimizedAssignmentServiceV3.updateProjectSubTaskStudentStatus(
        userId,
        classId,
        projectId,
        subTaskId,
        studentId,
        status,
        earnedPoints,
        memo,
        memberGroup
      );
      console.log(`✅ 최적화된 프로젝트 멤버 상태 업데이트 완료: ${projectId}-${subTaskId} - ${studentId} - ${status}`);
    } catch (error) {
      console.error('❌ 최적화된 프로젝트 멤버 상태 업데이트 실패:', error);
      throw error;
    }
  }

  // 여러 멤버의 하위 과제 상태를 배치로 업데이트 (성능 최적화)
  async batchUpdateSubTaskStudentStatuses(
    userId: string,
    classId: string,
    projectId: string,
    subTaskId: string,
    studentUpdates: Array<{
      studentId: string;
      status: AssignmentStatusValue;
      earnedPoints: number;
      memo?: string;
      submissionLink?: string;
      submissionMessage?: string;
      submittedAt?: Date;
      submissionImagePath?: string;
      submissionImageUpdatedAt?: string;
      submissionImageSizeBytes?: number;
    }>,
    memberGroup?: string
  ): Promise<void> {
    try {
      await optimizedAssignmentServiceV3.batchUpdateProjectSubTaskStudentStatuses(
        userId,
        classId,
        projectId,
        subTaskId,
        studentUpdates,
        memberGroup
      );
      console.log(`✅ 최적화된 프로젝트 멤버 상태 배치 업데이트 완료: ${projectId}-${subTaskId} - ${studentUpdates.length}명`);
    } catch (error) {
      console.error('❌ 최적화된 프로젝트 멤버 상태 배치 업데이트 실패:', error);
      throw error;
    }
  }
}

export const projectAssignmentService = new ProjectAssignmentService();
